import { NPool, NRelay1, type NostrFilter } from '@nostrify/nostrify';
import type { RelayMetadata } from '@/contexts/AppContext';

/**
 * App default relays. Used as the initial `relayMetadata` for new users and as
 * a fallback when the user has no NIP-65 relay list configured (e.g. during
 * nostrconnect handshakes before any user relays have been loaded).
 */
export const APP_RELAYS: RelayMetadata = {
  relays: [
    { url: 'wss://relay.bchnostr.com', read: true, write: true },
    { url: 'wss://relay.ditto.pub', read: true, write: true },
    { url: 'wss://relay.primal.net', read: true, write: true },
    { url: 'wss://relay.damus.io', read: true, write: true },
  ],
  updatedAt: 0,
};

/**
 * Discovery relays for merchant data.
 *
 * BCH Local is an aggregator: merchant listings must load from a fixed,
 * known-good set of relays regardless of the logged-in user's personal
 * NIP-65 relay list. Merchant queries always use this group (via
 * `nostr.group(DISCOVERY_RELAYS)`) instead of the default pool, so listings
 * never disappear when a user logs in with a different relay set.
 *
 * - relay.bchnostr.com  → BCHNostr business listings (kind 30499)
 * - relay.ditto.pub / primal / damus / nos.lol → BCHPAY.Cash listings (kind 35341)
 *
 * IMPORTANT: this set is deliberately kept in sync with the relays that
 * BCHNostr itself PUBLISHES business profiles to and QUERIES its own directory
 * from. If a business listing lands on a relay BCHNostr writes to but that is
 * missing here, that merchant silently never appears on BCH Local — which is
 * exactly the "listed on BCHNostr but not on the map" symptom. BCHNostr's core
 * publish set is relay.bchnostr.com / nos.lol / relay.primal.net /
 * relay.ditto.pub / relay.nostr.net, and its directory reads additionally fan
 * out to relay.nostr.band. We mirror both so nothing is dropped.
 */
export const DISCOVERY_RELAYS: string[] = [
  'wss://relay.bchnostr.com',
  'wss://relay.ditto.pub',
  'wss://relay.primal.net',
  'wss://relay.damus.io',
  'wss://nos.lol',
  // Added to match BCHNostr's own publish/directory relay set so every
  // BCHNostr business is discoverable here too:
  'wss://relay.nostr.net',
  'wss://relay.nostr.band',
];

/**
 * How long a discovery query should collect events across relays before
 * resolving. This is an *upper bound* — combined with the query's own abort
 * signal via `discoverySignal()` so a query never hangs forever.
 */
export const DISCOVERY_QUERY_TIMEOUT = 6000;

/**
 * Per-relay EOSE timeout for the discovery pool.
 *
 * This is the single biggest cause of a fluctuating merchant count. The app's
 * main pool uses a very short EOSE timeout (200ms) tuned for snappy
 * single-relay reads: a relay that hasn't sent EOSE within 200ms is treated as
 * "done" and its remaining events are dropped from that query. For an
 * aggregator that queries five relays at once, that means slower relays
 * routinely get cut off mid-response, so each run returns a slightly different
 * (partial) set and the count bounces around.
 *
 * The discovery pool below gives every relay a generous 3.5s to finish sending
 * before EOSE is assumed, so `query()` resolves with a complete, stable set.
 */
export const DISCOVERY_EOSE_TIMEOUT = 3500;

/**
 * Dedicated connection pool for merchant discovery reads.
 *
 * We deliberately do NOT reuse the app's main pool here. The main pool is
 * optimised for low-latency personal reads (200ms EOSE), which produces
 * partial, fluctuating results when fanning out across the discovery relay
 * set. This pool uses a long per-relay EOSE timeout so aggregation waits for
 * every relay to deliver its full result set.
 */
export const discoveryPool = new NPool({
  open(url: string) {
    return new NRelay1(url, { eoseTimeout: DISCOVERY_EOSE_TIMEOUT });
  },
  reqRouter(filters: NostrFilter[]) {
    const routes = new Map<string, NostrFilter[]>();
    for (const url of DISCOVERY_RELAYS) {
      routes.set(url, filters);
    }
    return routes;
  },
  eventRouter() {
    return [...DISCOVERY_RELAYS];
  },
});

/**
 * Combine a TanStack Query abort signal with a generous discovery timeout so
 * merchant queries collect events from all relays (not just the fastest one)
 * before resolving.
 */
export function discoverySignal(querySignal?: AbortSignal): AbortSignal {
  const timeout = AbortSignal.timeout(DISCOVERY_QUERY_TIMEOUT);
  if (!querySignal) return timeout;
  // Abort as soon as either the query is cancelled or the timeout fires.
  return AbortSignal.any([querySignal, timeout]);
}

let warmed = false;

/**
 * Eagerly open the discovery relay WebSocket connections.
 *
 * Establishing a relay connection involves a TCP + TLS + WebSocket handshake
 * that can cost hundreds of milliseconds — time that would otherwise be paid
 * on the very first merchant query while the user stares at a spinner. Calling
 * this at module load kicks off all the handshakes in parallel so the sockets
 * are (usually) already open by the time the queries fire, making the first
 * paint of the map noticeably faster.
 */
export function warmDiscoveryRelays(): void {
  if (warmed) return;
  warmed = true;
  for (const url of DISCOVERY_RELAYS) {
    try {
      // Accessing the relay via the pool lazily constructs the NRelay1, which
      // opens the underlying socket.
      discoveryPool.relay(url);
    } catch {
      // Ignore individual relay failures — the query fan-out tolerates them.
    }
  }
}

// Warm the connections as soon as this module is imported.
warmDiscoveryRelays();
